/**
 * DocSedIngestionPopover — SET-D4.
 *
 * Answers the user-reported question "How does SED ingest this
 * document?" by surfacing the same data the Site Evidence tab uses
 * internally:
 *
 *   - Include / exclude status (`SiteDocument.includeInSed`, KSD3)
 *   - SED variable contributions (reverse index — DocumentContentsPanel
 *     `buildReverseIndex(sed)`)
 *   - KSD6 extraction-cache key (the leading 12 chars of `contentHash`,
 *     when present — pre-content-hash uploads omit the line)
 *   - A one-line pipeline explainer pointing at `extractFromDocument()`
 *     and `memoisedExtractFromDocument` (KSD6) so the curious can
 *     drill in via the codebase
 *
 * Design notes:
 *  - Pure presentational. The reverse-index input is computed by the
 *    parent (DocumentLibrary / DocumentContentsPanel / DocViewerDialog)
 *    so the popover doesn't reach into the store directly. That keeps
 *    test setup tight (no store mocks needed) and matches how the
 *    panel already passes data down.
 *  - Section ids are rendered through `SED_SECTION_LABELS` so the user
 *    sees human-readable section names rather than the internal
 *    SedSectionId enum.
 *  - PL pack (linkedDocumentRefs richer reverse-index) is shipped — on
 *    pre-PL reports the contribution count gracefully shows 0 with a
 *    "Not yet extracted — will be on next Rebuild" hint.
 */
import React from 'react';
import { Info } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import type { SiteDocument } from '../../types/siteRag';
import type { SedSectionId } from '../../types/siteEvidence';
import { SED_SECTION_LABELS } from '../../lib/sedStaleness';

export interface DocSedIngestionPopoverProps {
  /** The document the popover describes. */
  doc: SiteDocument;
  /** SED sections this doc contributes to (from reverse index). */
  contributedSections: SedSectionId[];
  /** Optional — when present, rendered as a Re-extract affordance.
   *  Matches the existing KSD5 `reExtractDocForSed` handler signature
   *  used elsewhere. */
  onReExtract?: (doc: SiteDocument) => void | Promise<void>;
  /** Optional visual size tweak. The default suits dense doc-library
   *  rows; doc-viewer dialogs want a slightly larger trigger. */
  triggerSize?: 'sm' | 'md';
  /** Test id suffix so multiple popovers in one tree are addressable. */
  testIdSuffix?: string;
}

export const DocSedIngestionPopover: React.FC<DocSedIngestionPopoverProps> = ({
  doc,
  contributedSections,
  onReExtract,
  triggerSize = 'sm',
  testIdSuffix,
}) => {
  const isIncluded = doc.includeInSed !== false;
  const sectionCount = contributedSections.length;
  const iconCls = triggerSize === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const tid = testIdSuffix ?? doc.id;

  // Status copy — three branches matched to the three real states the
  // SED ingest pipeline can leave a doc in.
  const statusNode = !isIncluded ? (
    <span className="text-danger">
      Excluded from SED (toggle to include via the Document Library or the
      Document Contents panel)
    </span>
  ) : sectionCount === 0 ? (
    <span className="text-text-secondary">
      Not yet extracted — will be on next SED rebuild.
    </span>
  ) : (
    <span className="text-success">
      Extracted — contributing to {sectionCount}{' '}
      SED section{sectionCount === 1 ? '' : 's'}.
    </span>
  );

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="How is this document used by SED?"
            className="p-0.5 inline-flex items-center justify-center text-text-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            data-testid={`doc-sed-ingestion-trigger-${tid}`}
          >
            <Info className={iconCls} aria-hidden="true" />
          </button>
        }
      />
      <PopoverContent
        align="end"
        className="w-[320px] text-xs space-y-2 p-3"
        data-testid={`doc-sed-ingestion-content-${tid}`}
      >
        <div className="type-section text-text-secondary">
          How SED uses this document
        </div>

        <div>
          <span className="text-text-muted">Status: </span>
          {statusNode}
        </div>

        {sectionCount > 0 && (
          <div>
            <div className="text-text-muted mb-1">SED sections from this doc:</div>
            <ul
              className="space-y-0.5 max-h-[180px] overflow-auto pr-1"
              data-testid={`doc-sed-ingestion-sections-${tid}`}
            >
              {contributedSections.map(sid => (
                <li key={sid} className="font-mono text-[10px] text-text-primary">
                  {SED_SECTION_LABELS[sid] ?? sid}
                </li>
              ))}
            </ul>
          </div>
        )}

        {doc.contentHash && (
          <div className="text-[10px] text-text-muted">
            Cache key:{' '}
            <code className="font-mono text-text-secondary">
              {doc.contentHash.slice(0, 12)}…
            </code>{' '}
            (KSD6 extraction cache)
          </div>
        )}

        {onReExtract && isIncluded && (
          <button
            type="button"
            onClick={() => void onReExtract(doc)}
            className="text-[10px] text-primary hover:underline underline-offset-2"
            data-testid={`doc-sed-ingestion-re-extract-${tid}`}
          >
            Re-extract this document
          </button>
        )}

        <div className="border-t border-border pt-2 text-[10px] text-text-muted">
          SED ingests via{' '}
          <code className="font-mono">extractFromDocument()</code> (one Gemini
          call per applicable SED section); results cached by{' '}
          <code className="font-mono">memoisedExtractFromDocument</code> on{' '}
          <code className="font-mono">contentHash</code> + model version
          (KSD6).
        </div>
      </PopoverContent>
    </Popover>
  );
};
